import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';

import * as actions from './schedulingCalendarActions';
import * as services from './schedulingCalendarServices';
import * as types from './schedulingCalendarActionType';
import SweetAlert from '../../../components/sweetalert/SweetAlert';
import { fetchPublicSchedulingCalendarTaskFacilities } from '../publicView/services/publicSchedulingCalendarServices';

function* fetchSchedulingCalendarSaga(action) {
    try {
        const payload = action.payload || {};
        const requestPayload = {
            FromDate: payload.FromDate,
            ToDate: payload.ToDate,
            SelectedMonthStartDate: payload.SelectedMonthStartDate || null,
            SelectedMonthEndDate: payload.SelectedMonthEndDate || null,
            IncludeWeekNumberRows: payload.IncludeWeekNumberRows === true,
            WorklistIDs: payload.WorklistIDs || null,
            TaskIDs: payload.TaskIDs || null,
            TaskTags: payload.TaskTags || [],
            SiteIDs: payload.SiteIDs || null,
            FacilityTags: payload.FacilityTags || [],
            ProviderIDs: payload.ProviderIDs || null,
            ProviderTags: payload.ProviderTags || [],
            TaskLocationIDs: payload.TaskLocationIDs || null,
            ScheduleStatus: payload.ScheduleStatus ?? null,
        };

        const response = yield call(services.fetchSchedulingCalendar, requestPayload);
        if (response?.success) {
            yield put(
                actions.fetchSchedulingCalendarSuccess(
                    {
                        data: response?.values || response?.value || [],
                        cacheKey: payload.cacheKey || '',
                    },
                ),
            );
            return;
        }

        yield put(
            actions.fetchSchedulingCalendarFailure(
                response?.errorMessage || 'Failed to load scheduling calendar data.',
            ),
        );
    } catch (error) {
        yield put(
            actions.fetchSchedulingCalendarFailure(
                error?.message || 'Failed to load scheduling calendar data.',
            ),
        );
    }
}

function* fetchSchedulingCalendarMetadataSaga() {
    try {
        const [tasksResponse, radiologistsResponse] = yield call(
            services.fetchSchedulingCalendarMetadata,
        );
        yield put(
            actions.fetchSchedulingCalendarMetadataSuccess({
                tasks:
                    tasksResponse?.values?.taskList ||
                    tasksResponse?.values?.lstTask ||
                    tasksResponse?.values?.TaskConfiguration ||
                    tasksResponse?.values?.tasks ||
                    [],
                radiologists:
                    radiologistsResponse?.values?.RadiologistDropdownItems ||
                    radiologistsResponse?.values?.lstRadiologist ||
                    radiologistsResponse?.values?.radiologists ||
                    [],
            }),
        );
    } catch (error) {
        yield put(
            actions.fetchSchedulingCalendarMetadataFailure(
                error?.message || 'Failed to load scheduling calendar metadata.',
            ),
        );
    }
}

function* saveSchedulingCalendarShiftSaga(action) {
    try {
        const response = yield call(
            services.saveSchedulingCalendarShift,
            action.payload?.shiftPayload,
        );

        const responseId = Number(response?.value?.ResponseId ?? response?.values?.ResponseId ?? 0);
        const isSuccess = responseId === 1 || (response?.success === true && responseId === 0);

        if (isSuccess) {
            yield put(actions.saveSchedulingCalendarShiftSuccess());

            if (action.payload?.refreshPayload) {
                yield put(
                    actions.fetchSchedulingCalendarRequest(action.payload.refreshPayload),
                );
            }

            if (Array.isArray(action.payload?.refreshPayloads)) {
                for (const refreshPayload of action.payload.refreshPayloads) {
                    yield put(actions.fetchSchedulingCalendarRequest(refreshPayload));
                }
            }

            const successMessage =
                action.payload?.successMessage ||
                response?.resultMessage ||
                response?.message ||
                '';

            if (successMessage) {
                yield call(SweetAlert.success, {
                    text: successMessage,
                });
            }

            if (typeof action.payload?.onSuccess === 'function') {
                yield call(action.payload.onSuccess, response);
            }
            return;
        }

        const errorMessage =
            response?.errorMessage ||
            response?.message ||
            response?.value?.ExceptionId ||
            response?.values?.ExceptionId ||
            'Failed to save scheduling calendar shift.';

        yield put(actions.saveSchedulingCalendarShiftFailure(errorMessage));
        yield call(SweetAlert.error, { text: errorMessage });
    } catch (error) {
        const errorMessage =
            error?.message || 'Failed to save scheduling calendar shift.';
        yield put(actions.saveSchedulingCalendarShiftFailure(errorMessage));
        yield call(SweetAlert.error, { text: errorMessage });
    }
}

function* deleteSchedulingCalendarShiftSaga(action) {
    try {
        const response = yield call(
            services.deleteSchedulingCalendarShift,
            action.payload?.shiftId,
        );

        const responseId = Number(response?.value?.ResponseId ?? response?.values?.ResponseId ?? 0);
        const isSuccess = responseId === 1 || (response?.success === true && responseId === 0);

        if (isSuccess) {
            yield put(actions.deleteSchedulingCalendarShiftSuccess());

            if (action.payload?.refreshPayload) {
                yield put(
                    actions.fetchSchedulingCalendarRequest(action.payload.refreshPayload),
                );
            }

            if (Array.isArray(action.payload?.refreshPayloads)) {
                for (const refreshPayload of action.payload.refreshPayloads) {
                    yield put(actions.fetchSchedulingCalendarRequest(refreshPayload));
                }
            }

            if (action.payload?.successMessage) {
                yield call(SweetAlert.success, {
                    text: action.payload.successMessage,
                });
            }

            if (typeof action.payload?.onSuccess === 'function') {
                yield call(action.payload.onSuccess, response);
            }
            return;
        }

        const errorMessage =
            response?.errorMessage ||
            response?.message ||
            response?.value?.ExceptionId ||
            response?.values?.ExceptionId ||
            'Failed to delete scheduling calendar shift.';

        yield put(actions.deleteSchedulingCalendarShiftFailure(errorMessage));
        yield call(SweetAlert.error, { text: errorMessage });
    } catch (error) {
        const errorMessage =
            error?.message || 'Failed to delete scheduling calendar shift.';
        yield put(actions.deleteSchedulingCalendarShiftFailure(errorMessage));
        yield call(SweetAlert.error, { text: errorMessage });
    }
}

function* fetchSchedulingCalendarAdhocDropdownsSaga() {
    try {
        const response = yield call(services.fetchSchedulingCalendarAdhocDropdowns);
        const value = response?.value || response?.values || {};

        yield put(
            actions.fetchSchedulingCalendarAdhocDropdownsSuccess({
                taskTags: value?.TaskTags || value?.taskTags || [],
                providerTags: value?.ProviderTags || value?.providerTags || [],
                tasks: value?.Tasks || value?.tasks || [],
                providers: value?.Providers || value?.providers || [],
                facilityTags: value?.FacilityTags || value?.facilityTags || [],
                facilities: value?.Facilities || value?.facilities || [],
                taskLocations: value?.TaskLocations || value?.taskLocations || [],
                worklists: value?.Worklists || value?.worklists || [],
                recurrences: value?.Recurrences || value?.recurrences || [],
            }),
        );
    } catch (error) {
        yield put(
            actions.fetchSchedulingCalendarAdhocDropdownsFailure(
                error?.message || 'Failed to load scheduling calendar adhoc dropdowns.',
            ),
        );
    }
}

function* fetchTaskFacilitiesSaga(action) {
    const { owner, taskId: requestedTaskId, publicContext } = action.payload || {};
    try {
        // Public shared-link view has no auth token, so it must hit the anonymous endpoint instead.
        const response = publicContext
            ? yield call(fetchPublicSchedulingCalendarTaskFacilities, { ...publicContext, taskId: requestedTaskId })
            : yield call(services.fetchTaskFacilities, requestedTaskId);

        if (response?.success === true) {
            const value = response?.value || response?.values || {};
            const respondedTaskId = value?.TaskId ?? value?.taskId ?? requestedTaskId;
            const facilities = (value?.Facilities || value?.facilities || []).map((item) => ({
                facilityId: item?.FacilityId ?? item?.facilityId,
                facilityName: item?.FacilityName ?? item?.facilityName ?? '',
                isTaskAssociated: Boolean(item?.IsTaskAssociated ?? item?.isTaskAssociated),
                isProviderAssociated: Boolean(item?.IsProviderAssociated ?? item?.isProviderAssociated),
            }));

            yield put(actions.fetchTaskFacilitiesSuccess({ owner, taskId: respondedTaskId, facilities }));
            return;
        }

        // Silent failure by design: this is a passive, auto-triggered fetch behind a side panel,
        // not a user-initiated action -- surfacing a SweetAlert here would be noisy. Falling back
        // to "no facilities" (panel simply doesn't show) is the correct degradation.
        yield put(actions.fetchTaskFacilitiesFailure({
            owner,
            taskId: requestedTaskId,
            error: response?.errorMessage || 'Failed to load task facilities.',
        }));
    } catch (error) {
        yield put(actions.fetchTaskFacilitiesFailure({
            owner,
            taskId: requestedTaskId,
            error: error?.message || 'Failed to load task facilities.',
        }));
    }
}

function* checkSchedulingCalendarAdhocSaga(action) {
    try {
        const response = yield call(
            services.saveSchedulingCalendarAdhocShifts,
            {
                ...action.payload?.request,
                IsSave: false,
            },
        );

        const isSuccess = response?.success === true;
        const value = response?.value || response?.values;
        if (isSuccess && value) {
            yield put(actions.checkSchedulingCalendarAdhocSuccess(value));
            if (typeof action.payload?.onSuccess === 'function') {
                yield call(action.payload.onSuccess, value);
            }
            return;
        }

        const errorMessage = response?.errorMessage || 'Failed to check adhoc shift conflicts.';
        yield put(actions.checkSchedulingCalendarAdhocFailure(errorMessage));
        yield call(SweetAlert.error, { text: errorMessage });
    } catch (error) {
        const errorMessage = error?.message || 'Failed to check adhoc shift conflicts.';
        yield put(actions.checkSchedulingCalendarAdhocFailure(errorMessage));
        yield call(SweetAlert.error, { text: errorMessage });
    }
}

function* saveSchedulingCalendarAdhocSaga(action) {
    try {
        const response = yield call(
            services.saveSchedulingCalendarAdhocShifts,
            {
                ...action.payload?.request,
                IsSave: true,
            },
        );

        const responseId = Number(response?.value?.ResponseId ?? response?.values?.ResponseId ?? 0);
        const isSuccess = response?.success === true || responseId === 1;
        const value = response?.value || response?.values;
        const hasConflicts = Number(value?.ConflictsCount ?? value?.conflictsCount ?? 0) > 0;
        const isSaved = value?.IsSaved === true;
        const successMessage =
            response?.resultMessage || action.payload?.successMessage || 'Adhoc shifts saved successfully.';

        if (isSuccess && value) {
            yield put(actions.saveSchedulingCalendarAdhocSuccess(value));

            if (isSaved && Array.isArray(action.payload?.refreshPayloads)) {
                for (const refreshPayload of action.payload.refreshPayloads) {
                    yield put(actions.fetchSchedulingCalendarRequest(refreshPayload));
                }
            }

            if (isSaved && successMessage) {
                yield call(SweetAlert.success, {
                    text: successMessage,
                });
            }

            if (!isSaved && hasConflicts && action.payload?.showConflictAlertText) {
                yield call(SweetAlert.error, { text: action.payload.showConflictAlertText });
            }

            if (typeof action.payload?.onSuccess === 'function') {
                yield call(action.payload.onSuccess, value);
            }
            return;
        }

        const errorMessage = response?.errorMessage || 'Failed to save adhoc shifts.';
        yield put(actions.saveSchedulingCalendarAdhocFailure(errorMessage));
        yield call(SweetAlert.error, { text: errorMessage });
    } catch (error) {
        const errorMessage = error?.message || 'Failed to save adhoc shifts.';
        yield put(actions.saveSchedulingCalendarAdhocFailure(errorMessage));
        yield call(SweetAlert.error, { text: errorMessage });
    }
}

function* generateSchedulingCalendarSaga(action) {
    try {
        const response = yield call(
            services.generateSchedulingCalendarShifts,
            action.payload?.request,
        );

        const responseId = Number(response?.value?.ResponseId ?? response?.values?.ResponseId ?? 0);
        const isSuccess = response?.success === true || responseId === 1;
        const value = response?.value || response?.values;

        if (isSuccess && value) {
            yield put(actions.generateSchedulingCalendarSuccess(value));

            if (typeof action.payload?.onSuccess === 'function') {
                yield call(action.payload.onSuccess, value);
            }

            if (action.payload?.successMessage) {
                yield call(SweetAlert.success, {
                    text: action.payload.successMessage,
                });
            }

            return;
        }

        const errorMessage =
            response?.value?.ExceptionId
            || response?.values?.ExceptionId
            || response?.errorMessage
            || 'Failed to generate schedules.';
        yield put(actions.generateSchedulingCalendarFailure(errorMessage));
        yield call(SweetAlert.error, { text: errorMessage });
    } catch (error) {
        const errorMessage = error?.message || 'Failed to generate schedules.';
        yield put(actions.generateSchedulingCalendarFailure(errorMessage));
        yield call(SweetAlert.error, { text: errorMessage });
    }
}

function* createSchedulingCalendarSharableLinkSaga(action) {
    try {
        const response = yield call(
            services.createSchedulingCalendarSharableLink,
            action.payload?.request,
        );

        const value = response?.value || response?.values || {};
        const responseId = Number(value?.ResponseId ?? 0);
        const isSuccess = response?.success === true || responseId === 1;

        if (isSuccess) {
            yield put(actions.createSchedulingCalendarSharableLinkSuccess(value));

            if (typeof action.payload?.onSuccess === 'function') {
                yield call(action.payload.onSuccess, value);
            }

            return;
        }

        const errorMessage = response?.errorMessage || 'Failed to create sharable link.';
        yield put(actions.createSchedulingCalendarSharableLinkFailure(errorMessage));
        yield call(SweetAlert.error, { text: errorMessage });
    } catch (error) {
        const errorMessage = error?.message || 'Failed to create sharable link.';
        yield put(actions.createSchedulingCalendarSharableLinkFailure(errorMessage));
        yield call(SweetAlert.error, { text: errorMessage });
    }
}

function* sendSchedulingCalendarSharableLinkEmailSaga(action) {
    try {
        const response = yield call(
            services.sendSchedulingCalendarSharableLinkEmail,
            action.payload?.request,
        );

        const value = response?.value || response?.values || {};
        const responseId = Number(value?.ResponseId ?? 0);
        const isSuccess = response?.success === true || responseId === 1;

        if (isSuccess) {
            yield put(actions.sendSchedulingCalendarSharableLinkEmailSuccess(value));
            yield call(SweetAlert.success, {
                text: response?.resultMessage || 'Sharable link email sent successfully.',
            });

            if (typeof action.payload?.onSuccess === 'function') {
                yield call(action.payload.onSuccess, value);
            }

            return;
        }

        const errorMessage = response?.errorMessage || 'Failed to send sharable link email.';
        yield put(actions.sendSchedulingCalendarSharableLinkEmailFailure(errorMessage));
        yield call(SweetAlert.error, { text: errorMessage });
    } catch (error) {
        const errorMessage = error?.message || 'Failed to send sharable link email.';
        yield put(actions.sendSchedulingCalendarSharableLinkEmailFailure(errorMessage));
        yield call(SweetAlert.error, { text: errorMessage });
    }
}

function* fetchSchedulingCalendarSharableLinksSaga() {
    try {
        const response = yield call(services.fetchSchedulingCalendarSharableLinks);

        if (response?.success === true) {
            yield put(actions.fetchSchedulingCalendarSharableLinksSuccess(response?.value || response?.values || []));
            return;
        }

        const errorMessage = response?.errorMessage || 'Failed to load sharable links.';
        yield put(actions.fetchSchedulingCalendarSharableLinksFailure(errorMessage));
        yield call(SweetAlert.error, { text: errorMessage });
    } catch (error) {
        const errorMessage = error?.message || 'Failed to load sharable links.';
        yield put(actions.fetchSchedulingCalendarSharableLinksFailure(errorMessage));
        yield call(SweetAlert.error, { text: errorMessage });
    }
}

function* fetchSchedulingCalendarSharableLinkEmailAuditSaga(action) {
    try {
        const response = yield call(
            services.fetchSchedulingCalendarSharableLinkEmailAudit,
            action.payload?.sharableLinkId,
        );

        if (response?.success === true) {
            const rows = response?.value || response?.values || [];
            yield put(actions.fetchSchedulingCalendarSharableLinkEmailAuditSuccess(rows));

            if (typeof action.payload?.onSuccess === 'function') {
                yield call(action.payload.onSuccess, rows);
            }

            return;
        }

        const errorMessage = response?.errorMessage || 'Failed to load sharable link email audit log.';
        yield put(actions.fetchSchedulingCalendarSharableLinkEmailAuditFailure(errorMessage));
        yield call(SweetAlert.error, { text: errorMessage });
    } catch (error) {
        const errorMessage = error?.message || 'Failed to load sharable link email audit log.';
        yield put(actions.fetchSchedulingCalendarSharableLinkEmailAuditFailure(errorMessage));
        yield call(SweetAlert.error, { text: errorMessage });
    }
}

function* deleteSchedulingCalendarSharableLinkSaga(action) {
    try {
        const response = yield call(
            services.deleteSchedulingCalendarSharableLink,
            action.payload?.sharableLinkId,
        );

        const value = response?.value || response?.values || {};
        const responseId = Number(value?.ResponseId ?? 0);
        const isSuccess = response?.success === true || responseId === 1;

        if (isSuccess) {
            yield put(actions.deleteSchedulingCalendarSharableLinkSuccess(value));
            yield call(SweetAlert.success, {
                text: response?.resultMessage || 'Sharable link deleted successfully.',
            });

            if (typeof action.payload?.onSuccess === 'function') {
                yield call(action.payload.onSuccess, value);
            }

            return;
        }

        const errorMessage = response?.errorMessage || 'Failed to delete sharable link.';
        yield put(actions.deleteSchedulingCalendarSharableLinkFailure(errorMessage));
        yield call(SweetAlert.error, { text: errorMessage });
    } catch (error) {
        const errorMessage = error?.message || 'Failed to delete sharable link.';
        yield put(actions.deleteSchedulingCalendarSharableLinkFailure(errorMessage));
        yield call(SweetAlert.error, { text: errorMessage });
    }
}

function* updateSchedulingCalendarSharableLinkDateRangeSaga(action) {
    try {
        const response = yield call(
            services.updateSchedulingCalendarSharableLinkDateRange,
            action.payload?.request,
        );

        const value = response?.value || response?.values || {};
        const responseId = Number(value?.ResponseId ?? 0);
        const isSuccess = response?.success === true || responseId === 1;

        if (isSuccess) {
            yield put(actions.updateSchedulingCalendarSharableLinkDateRangeSuccess(value));

            if (typeof action.payload?.onSuccess === 'function') {
                yield call(action.payload.onSuccess, value);
            }

            return;
        }

        const errorMessage = response?.errorMessage || 'Failed to update date range.';
        yield put(actions.updateSchedulingCalendarSharableLinkDateRangeFailure(errorMessage));
        yield call(SweetAlert.error, { text: errorMessage });
    } catch (error) {
        const errorMessage = error?.message || 'Failed to update date range.';
        yield put(actions.updateSchedulingCalendarSharableLinkDateRangeFailure(errorMessage));
        yield call(SweetAlert.error, { text: errorMessage });
    }
}

function* schedulingCalendarSaga() {
    yield all([
        takeEvery(
            types.FETCH_SCHEDULING_CALENDAR_REQUEST,
            fetchSchedulingCalendarSaga,
        ),
        takeLatest(
            types.FETCH_SCHEDULING_CALENDAR_METADATA_REQUEST,
            fetchSchedulingCalendarMetadataSaga,
        ),
        takeLatest(
            types.SAVE_SCHEDULING_CALENDAR_SHIFT_REQUEST,
            saveSchedulingCalendarShiftSaga,
        ),
        takeLatest(
            types.DELETE_SCHEDULING_CALENDAR_SHIFT_REQUEST,
            deleteSchedulingCalendarShiftSaga,
        ),
        takeLatest(
            types.FETCH_SCHEDULING_CALENDAR_ADHOC_DROPDOWNS_REQUEST,
            fetchSchedulingCalendarAdhocDropdownsSaga,
        ),
        // takeEvery (not takeLatest): the addShift/assignProviders/editShift owners can have
        // legitimately-concurrent in-flight requests, and a single takeLatest watcher would cancel
        // one owner's request just because another owner dispatched afterward. Out-of-order/stale
        // response protection instead lives in the reducer (per-owner taskId match check).
        takeEvery(
            types.FETCH_TASK_FACILITIES_REQUEST,
            fetchTaskFacilitiesSaga,
        ),
        takeLatest(
            types.CHECK_SCHEDULING_CALENDAR_ADHOC_REQUEST,
            checkSchedulingCalendarAdhocSaga,
        ),
        takeLatest(
            types.SAVE_SCHEDULING_CALENDAR_ADHOC_REQUEST,
            saveSchedulingCalendarAdhocSaga,
        ),
        takeLatest(
            types.GENERATE_SCHEDULING_CALENDAR_REQUEST,
            generateSchedulingCalendarSaga,
        ),
        takeLatest(
            types.CREATE_SCHEDULING_CALENDAR_SHARABLE_LINK_REQUEST,
            createSchedulingCalendarSharableLinkSaga,
        ),
        takeLatest(
            types.SEND_SCHEDULING_CALENDAR_SHARABLE_LINK_EMAIL_REQUEST,
            sendSchedulingCalendarSharableLinkEmailSaga,
        ),
        takeLatest(
            types.FETCH_SCHEDULING_CALENDAR_SHARABLE_LINKS_REQUEST,
            fetchSchedulingCalendarSharableLinksSaga,
        ),
        takeLatest(
            types.FETCH_SCHEDULING_CALENDAR_SHARABLE_LINK_EMAIL_AUDIT_REQUEST,
            fetchSchedulingCalendarSharableLinkEmailAuditSaga,
        ),
        takeLatest(
            types.DELETE_SCHEDULING_CALENDAR_SHARABLE_LINK_REQUEST,
            deleteSchedulingCalendarSharableLinkSaga,
        ),
        takeLatest(
            types.UPDATE_SCHEDULING_CALENDAR_SHARABLE_LINK_DATE_RANGE_REQUEST,
            updateSchedulingCalendarSharableLinkDateRangeSaga,
        ),
    ]);
}

export default schedulingCalendarSaga;